import dayjs from 'dayjs'
import {
  IDevice,
  IDeviceAntenna,
  IRequestDeviceAntenna,
  IRequestEquipment,
  IRequestStationClass,
} from '../types/equipment.type'
import { IExercise, IRequestFrequencyGroup, IRequestFrequencyExclusion } from '../types/exercise.type'
import {
  IArcDiagramNodes,
  IFrequency,
  IDeviceAntennaMode,
  IDeviceTransmitter,
  IDeviceTransmitterPulse,
  IFrequencyPlan,
  IFrequencyPool,
  IFrequencyPool_113_114_115,
  IFrequencyPoolTableEquipment,
  IHopset,
  ILocationTemplateWithRegions,
  IOpenFrequencyRequest,
  IPlatform,
} from '../types/spectrum-manager.type'
import { IResponse } from '../types/response.type'
import { axiosInstance } from './axios.service'
import { IAgencyOverride } from '../../shared/types/api/user.type'
import { RequestFormEndpoints, SpectrumManagerEndpoints } from '../types/endpoint.type'
import { ISFAFContents, ISFAFFrequency } from '../types/sfaf.type'
import {
  ISpecManRequestFrequency,
  ISFAFFrequencyResponse,
  ISFAFStationClassResponse,
  IUpdateFrequencyRequest,
} from '../../shared/types/api/spectrum-manager.type'

// Properties the UI layers onto entity objects (DataGrid row id, entity PK,
// nested relation arrays, db-managed timestamps) that the backend validators
// reject. Stripped from every write payload on the way out. Callers can pass
// additional endpoint-specific keys via `extras` (e.g. parent FKs that the
// URL already encodes).
const SPURIOUS_PROPS = ['id', 'tableId', 'items', 'created', 'created_at', 'updated_at'] as const

const stripSpurious = <T extends object>(params: T, extras: readonly string[] = []): Partial<T> => {
  const out: any = { ...params }
  for (const k of SPURIOUS_PROPS) delete out[k]
  for (const k of extras) delete out[k]
  return out
}

export const getAllDetailedRequests = async (
  params: {
    status?: string
    exerciseId?: number
    locationId?: number
  },
  signal: AbortSignal,
): Promise<IResponse<ISpecManRequestFrequency[]>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.REQUESTS, {
      params,
      signal,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateFrequencyRequest = async (
  frequencyGroupId: number,
  radius_306?: string | null,
  radius_406?: string | null,
  number_of_frequencies?: number | null,
): Promise<IResponse<IRequestFrequencyGroup>> => {
  try {
    const data = await axiosInstance.put(SpectrumManagerEndpoints.FREQUENCY_REQUEST, {
      frequencyGroupId,
      radius_306,
      radius_406,
      number_of_frequencies,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllFrequencyPlans = async (params: {
  status?: string
  exerciseId?: number
  locationId?: number
}): Promise<IResponse<IFrequencyPlan[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_FREQUENCY_PLANS, {
      params,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllLocationTemplatesWithRegions = async (): Promise<IResponse<ILocationTemplateWithRegions[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_LOCATION_TEMPLATES_WITH_REGIONS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllPlatforms = async (): Promise<IResponse<IPlatform[]>> => {
  try {
    const data = await axiosInstance.get(RequestFormEndpoints.GET_PLATFORMS)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getEquipmentsByFrequencyGroupId = async (id: number): Promise<IResponse<IRequestEquipment[]>> => {
  try {
    const data = await axiosInstance.get(`${SpectrumManagerEndpoints.EQUIPMENT}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateEquipment = async (id: number, params: IRequestEquipment): Promise<IResponse<IRequestEquipment>> => {
  try {
    const data = await axiosInstance.put(`${SpectrumManagerEndpoints.EQUIPMENT}/${id}`, stripSpurious(params))

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getFrequenciesByFrequencyGroupId = async (id: number): Promise<IResponse<IFrequency[]>> => {
  try {
    const data = await axiosInstance.get(`${RequestFormEndpoints.GET_REQUEST_FREQUENCIES}/${id}/request-frequencies`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateFrequency = async (id: number, params: IUpdateFrequencyRequest): Promise<IResponse<IFrequency>> => {
  try {
    const resp = await axiosInstance.put(`${SpectrumManagerEndpoints.FREQUENCY}/${id}`, stripSpurious(params))

    return resp.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createFrequencyForGroup = async (
  exerciseRequestFrequencyGroupId: number,
): Promise<IResponse<IFrequency>> => {
  try {
    const data = await axiosInstance.post(SpectrumManagerEndpoints.FREQUENCY, {
      exerciseRequestFrequencyGroupId,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getFrequencyGroup = async (id: number): Promise<IResponse<IRequestFrequencyGroup>> => {
  try {
    const data = await axiosInstance.get(`${SpectrumManagerEndpoints.FREQUENCY_GROUPS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateFrequencyGroup = async (
  id: number,
  params: IRequestFrequencyGroup,
): Promise<IResponse<IRequestFrequencyGroup>> => {
  try {
    const data = await axiosInstance.put(`${SpectrumManagerEndpoints.FREQUENCY_GROUPS}/${id}`, stripSpurious(params))

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllSFAFFrequencies = async (params: {
  exerciseId?: number
  locationId?: number
  requestId?: number
  requestFrequencyGroupId?: number
}): Promise<IResponse<ISFAFFrequency[]>> => {
  try {
    const resp = await axiosInstance.get(SpectrumManagerEndpoints.SFAF_FREQUENCIES, {
      params,
    })

    // TODO: resp.data is a actually a IResponse<Serialized<ISFAFFrequencyResponse[]>>.
    // I don't think our Serialized type is working quite right
    const body: IResponse<ISFAFFrequencyResponse[]> = resp.data
    if (!body.success) throw new Error(body.message) // satisfy tsc

    // Convert strings to dayjs objects:
    return {
      ...body,
      data: body.data!.map((freq) => ({
        ...freq,
        start_date: freq.start_date && dayjs(freq.start_date).utc(true),
        end_date: freq.end_date && dayjs(freq.end_date).utc(true),
        request_frequency_created_at:
          freq.request_frequency_created_at && dayjs(freq.request_frequency_created_at).utc(true),
        request_location_created_at:
          freq.request_location_created_at && dayjs(freq.request_location_created_at).utc(true),
        receive_locations: freq.receive_locations.map((rl) => ({
          ...rl,
          receive_request_location_created_at:
            rl.receive_request_location_created_at && dayjs(rl.receive_request_location_created_at).utc(true),
        })),
      })),
    }
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllSFAFEquipments = async (params: {
  exerciseId?: number
  locationId?: number
}): Promise<IResponse<IRequestEquipment[]>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.SFAF_EQUIPMENTS, {
      params,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllSFAFExcludedFrequencies = async (params: {
  exerciseId?: number
  locationId?: number
}): Promise<IResponse<IRequestFrequencyExclusion[]>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.SFAF_EXCLUDED_FREQUENCIES, {
      params,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllSFAFAntennas = async (params: {
  exerciseId?: number
  locationId?: number
}): Promise<IResponse<Array<IRequestDeviceAntenna & { code: string }>>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.SFAF_ANTENNAS, {
      params,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllSFAFContents = async (params: {
  exerciseId?: number
  locationId?: number
}): Promise<IResponse<ISFAFContents[]>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.SFAF_CONTENTS, {
      params,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllSFAFStationClasses = async (params: {
  exerciseId?: number
  locationId?: number
}): Promise<
  IResponse<
    Array<
      IRequestStationClass & {
        station: string | null
        '113_output': string | null
        emission_designator_simple_type_name: string | null
        emission_designator_name?: string | null
      }
    >
  >
> => {
  try {
    const resp = await axiosInstance.get(SpectrumManagerEndpoints.SFAF_STATION_CLASSES, {
      params,
    })

    const body: IResponse<ISFAFStationClassResponse[]> = resp.data
    if (!body.success) throw new Error(body.message) // satisfty tsc

    // TODO: Convert strings to dayjs objects:
    // TODO: dayjs would be better
    return body
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getSfafAgencyOverride = async (userId: number): Promise<IResponse<IAgencyOverride>> => {
  try {
    const data = await axiosInstance.get(`${SpectrumManagerEndpoints.AGENCY_OVERRIDE}/${userId}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllHopsets = async (): Promise<IResponse<IHopset[]>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.HOPSET, {})

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllArcDiagramNodes = async (params: {
  exerciseId?: number
  locationId?: number
  status?: string
}): Promise<IResponse<IArcDiagramNodes[]>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.ARC_DIAGRAM_NODES, {
      params,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createLocationTemplate = async (
  params: ILocationTemplateWithRegions,
): Promise<IResponse<ILocationTemplateWithRegions>> => {
  try {
    const data = await axiosInstance.post(SpectrumManagerEndpoints.LOCATION_TEMPLATES, params)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllLicenses = async (params: {
  exerciseId: number
  start_freq_long: number
  end_freq_long: number
}): Promise<IResponse<IFrequencyPool[]>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.LICENSE, {
      params,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllOpenFrequency = async (params: {
  locationId?: number
  status?: string
  freqStatus?: string
  exerciseId?: number
}): Promise<IResponse<IOpenFrequencyRequest[]>> => {
  try {
    const data = await axiosInstance.get(SpectrumManagerEndpoints.OPEN_FREQUENCY_REQUEST, {
      params,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteLocationTemplate = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${SpectrumManagerEndpoints.LOCATION_TEMPLATES}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateRequestStatus = async (id: number, status: string): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.put(`${SpectrumManagerEndpoints.REQUESTS}/${id}`, {
      status,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createExercise = async (params: IExercise): Promise<IResponse<IExercise>> => {
  try {
    const data = await axiosInstance.post(SpectrumManagerEndpoints.EXERCISE, params)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteExercise = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${SpectrumManagerEndpoints.EXERCISE}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllDeviceTransmitter = async (device_id: number): Promise<IResponse<IDeviceTransmitter[]>> => {
  try {
    const data = await axiosInstance.get(`${SpectrumManagerEndpoints.DEVICE_TRANSMITTER}/${device_id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createDeviceTransmitter = async (device_id: number): Promise<IResponse<IDeviceTransmitter>> => {
  try {
    const data = await axiosInstance.post(`${SpectrumManagerEndpoints.DEVICE_TRANSMITTER}/${device_id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateDeviceTransmitter = async (
  id: number,
  params: IDeviceTransmitter,
): Promise<IResponse<IDeviceTransmitter>> => {
  try {
    const data = await axiosInstance.put(
      `${SpectrumManagerEndpoints.DEVICE_TRANSMITTER}/${id}`,
      stripSpurious(params, ['device_id']),
    )

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteDeviceTransmitter = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${SpectrumManagerEndpoints.DEVICE_TRANSMITTER}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createDeviceAntenna = async (device_id: number): Promise<IResponse<IDeviceAntenna>> => {
  try {
    const data = await axiosInstance.post(`${SpectrumManagerEndpoints.DEVICE_ANTENNA}/${device_id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateDeviceAntenna = async (id: number, params: IDeviceAntenna): Promise<IResponse<IDeviceAntenna>> => {
  try {
    const data = await axiosInstance.put(
      `${SpectrumManagerEndpoints.DEVICE_ANTENNA}/${id}`,
      stripSpurious(params, ['device_id', 'serial', 'antenna_type']),
    )

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteDeviceAntenna = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${SpectrumManagerEndpoints.DEVICE_ANTENNA}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllDeviceTransmitterPulse = async (
  device_transmitter_id: number,
): Promise<IResponse<IDeviceTransmitterPulse[]>> => {
  try {
    const data = await axiosInstance.get(
      `${SpectrumManagerEndpoints.DEVICE_TRANSMITTER_PULSE}/${device_transmitter_id}`,
    )

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createDeviceTransmitterPulse = async (
  device_transmitter_id: number,
): Promise<IResponse<IDeviceTransmitterPulse>> => {
  try {
    const data = await axiosInstance.post(
      `${SpectrumManagerEndpoints.DEVICE_TRANSMITTER_PULSE}/${device_transmitter_id}`,
    )

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateDeviceTransmitterPulse = async (
  id: number,
  params: IDeviceTransmitterPulse,
): Promise<IResponse<IDeviceTransmitterPulse>> => {
  try {
    const data = await axiosInstance.put(
      `${SpectrumManagerEndpoints.DEVICE_TRANSMITTER_PULSE}/${id}`,
      stripSpurious(params),
    )

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteDeviceTransmitterPulse = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${SpectrumManagerEndpoints.DEVICE_TRANSMITTER_PULSE}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllDeviceAntennaModes = async (device_antenna_id: number): Promise<IResponse<IDeviceAntennaMode[]>> => {
  try {
    const data = await axiosInstance.get(`${SpectrumManagerEndpoints.DEVICE_ANTENNA_MODE}/${device_antenna_id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createDeviceAntennaMode = async (device_antenna_id: number): Promise<IResponse<IDeviceAntennaMode>> => {
  try {
    const data = await axiosInstance.post(`${SpectrumManagerEndpoints.DEVICE_ANTENNA_MODE}/${device_antenna_id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateDeviceAntennaMode = async (
  id: number,
  params: IDeviceAntennaMode,
): Promise<IResponse<IDeviceAntennaMode>> => {
  try {
    const data = await axiosInstance.put(`${SpectrumManagerEndpoints.DEVICE_ANTENNA_MODE}/${id}`, stripSpurious(params))

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteDeviceAntennaMode = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${SpectrumManagerEndpoints.DEVICE_ANTENNA_MODE}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createPlatform = async (params: IPlatform): Promise<IResponse<IPlatform>> => {
  try {
    const data = await axiosInstance.post(SpectrumManagerEndpoints.PLATFORMS, stripSpurious(params))

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updatePlatform = async (id: number, params: IPlatform): Promise<IResponse<IPlatform>> => {
  try {
    const data = await axiosInstance.put(`${SpectrumManagerEndpoints.PLATFORMS}/${id}`, stripSpurious(params))

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deletePlatform = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${SpectrumManagerEndpoints.PLATFORMS}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const createDevice = async (params: IDevice): Promise<IResponse<IDevice>> => {
  try {
    const data = await axiosInstance.post(SpectrumManagerEndpoints.DEVICE, stripSpurious(params))

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateDevice = async (id: number, params: IDevice): Promise<IResponse<IDevice>> => {
  try {
    const data = await axiosInstance.put(`${SpectrumManagerEndpoints.DEVICE}/${id}`, stripSpurious(params))

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const deleteDevice = async (id: number): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.delete(`${SpectrumManagerEndpoints.DEVICE}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllFrequencyPoolTableEquipments = async (
  id: number,
): Promise<IResponse<IFrequencyPoolTableEquipment[]>> => {
  try {
    const data = await axiosInstance.get(`${SpectrumManagerEndpoints.FREQUENCY_POOL_EQUIPMENT}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const getAllFrequencyPool_113_114_115 = async (id: number): Promise<IResponse<IFrequencyPool_113_114_115[]>> => {
  try {
    const data = await axiosInstance.get(`${SpectrumManagerEndpoints.FREQUENCY_POOL_113_114_115}/${id}`)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const nominateFrequency = async (
  id: number,
  params: {
    frequency_pool_id: number
    field_110_from: string
    value_144: string
    field_102: string
  },
): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.put(`${SpectrumManagerEndpoints.NOMINATE_FREQUENCY}/${id}`, params)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const updateSfaf = async (
  id: number,
  params: {
    sfaf: string
  },
): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.put(`${SpectrumManagerEndpoints.SFAF}/${id}`, params)

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}

export const importSfaf = async (
  fieldsData: {
    [key: string]: string
  }[],
): Promise<IResponse<null>> => {
  try {
    const data = await axiosInstance.post(SpectrumManagerEndpoints.SFAF, {
      fieldsData,
    })

    return data.data
  } catch (err: any) {
    return {
      success: false,
      message: err?.response?.data?.message ?? err.message,
    }
  }
}
